package com.arena_web.data.api

import com.arena_web.data.dto.FootballCompetitionDto
import com.arena_web.data.dto.FootballMatchDto
import com.arena_web.data.dto.FootballStandingsDto
import com.arena_web.data.dto.HeadToHeadDto
import com.arena_web.data.dto.MatchDetailDto
import com.arena_web.data.dto.PlayerDetailDto
import com.arena_web.data.dto.TeamDetailDto
import com.arena_web.data.http.ApiResponse
import com.arena_web.data.http.apiGet
import io.ktor.client.*

/**
 * Multi-sport match center — `v1/sports/{sport}/…` (football | basketball |
 * tennis | volleyball). Football aggregates football-data.org + TheSportsDB
 * server-side; the other sports are TheSportsDB alone.
 */
class FootballApi(private val client: HttpClient) {

    suspend fun getCompetitions(sport: String): ApiResponse<List<FootballCompetitionDto>> {
        return client.apiGet("v1/sports/$sport/competitions")
    }

    /** [date] = yyyy-MM-dd (UTC day); [competition] = optional code filter (e.g. "PL"). */
    suspend fun getMatches(sport: String, date: String, competition: String? = null): ApiResponse<List<FootballMatchDto>> {
        val params = buildMap {
            put("date", date)
            competition?.let { put("competition", it) }
        }
        return client.apiGet("v1/sports/$sport/matches", params)
    }

    suspend fun getStandings(sport: String, code: String): ApiResponse<FootballStandingsDto> {
        return client.apiGet("v1/sports/$sport/standings/$code")
    }

    /** Nearest upcoming day with matches after [from] — {date, count}. */
    suspend fun getNextMatchday(sport: String, from: String): ApiResponse<Map<String, String>> {
        return client.apiGet("v1/sports/$sport/matches/next", mapOf("from" to from))
    }

    /** Lineups / timeline / stats / highlight. Context params let the
     *  backend resolve football-data ids into TheSportsDB events. */
    suspend fun getMatchDetail(
        sport: String,
        id: String,
        date: String,
        competition: String?,
        home: String?,
        away: String?,
    ): ApiResponse<MatchDetailDto> {
        val params = buildMap {
            put("date", date)
            competition?.takeIf { it.isNotBlank() }?.let { put("competition", it) }
            home?.takeIf { it.isNotBlank() }?.let { put("home", it) }
            away?.takeIf { it.isNotBlank() }?.let { put("away", it) }
        }
        return client.apiGet("v1/sports/$sport/matches/$id/detail", params)
    }

    /** Past meetings of a football-data match's clubs (404 for TSDB ids). */
    suspend fun getHeadToHead(sport: String, id: String): ApiResponse<HeadToHeadDto> {
        return client.apiGet("v1/sports/$sport/matches/$id/h2h")
    }

    /** Player bio card. */
    suspend fun getPlayer(sport: String, playerId: String, locale: String?): ApiResponse<PlayerDetailDto> {
        val params = buildMap { locale?.takeIf { it.isNotBlank() }?.let { put("locale", it) } }
        return client.apiGet("v1/sports/$sport/players/$playerId", params)
    }

    /** Club profile + squad + recent/upcoming matches. */
    suspend fun getTeamDetail(sport: String, teamId: String, locale: String?): ApiResponse<TeamDetailDto> {
        val params = buildMap { locale?.takeIf { it.isNotBlank() }?.let { put("locale", it) } }
        return client.apiGet("v1/sports/$sport/teams/$teamId", params)
    }
}
